/**
 * Angular Combobox Component
 *
 * Migrated subset of the AngularJS `data-combobox` directive
 * (apps/b2b-ui/common/directives/combobox/combobox.{js,tpl.html,less}).
 *
 * Renders a Bootstrap-styled input-group: a text input with a floating label
 * and a caret-down button that toggles a dropdown listing `items`. While typing
 * (when `filterable` is true) the list is filtered by the string returned by
 * `displayWith`. Selecting an item emits `selectionChange` and resets the
 * internal query.
 *
 * Per-item rendering is provided by content projection — consumers pass an
 * <ng-template #itemTemplate let-item> with the markup to use for each row.
 * When no template is provided a default row with `displayWith(item)` is used.
 *
 * Scope of the migration (Option B): items, name, label, disabled, filterable,
 * dropdownClass, displayWith + itemTemplate + selectionChange. Other legacy
 * features (append-to-body, required/errors/validtest, search-action,
 * wait-time, add-new, on-blur/on-focus, icon, button-icon, remove-on-clear)
 * are deferred to follow-ups and will be added when their first Angular
 * consumer is migrated.
 *
 * Usage in Angular templates:
 *   <acme-combobox [items]="rows" label="Choose one" [(query)]="q"
 *                  (selectionChange)="onPick($event)">
 *     <ng-template #itemTemplate let-item>
 *       {{ item.name }} — {{ item.price | currency }}
 *     </ng-template>
 *   </acme-combobox>
 */
import {
  Component,
  ContentChild,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  Output,
  TemplateRef,
  computed,
  inject,
  signal,
} from '@angular/core';
import { NgFor, NgIf, NgTemplateOutlet } from '@angular/common';
import { FormsModule } from '@angular/forms';

@Component({
  selector: 'acme-combobox',
  standalone: true,
  imports: [NgIf, NgFor, NgTemplateOutlet, FormsModule],
  template: require('./combobox.component.html'),
  styles: [require('./combobox.component.less')],
})
export class ComboboxComponent<T = unknown> {
  private readonly _items = signal<T[]>([]);
  @Input()
  get items(): T[] { return this._items(); }
  set items(value: T[]) { this._items.set(value); }

  private readonly _label = signal('');
  @Input()
  get label(): string { return this._label(); }
  set label(value: string) { this._label.set(value); }

  private readonly _name = signal('');
  @Input()
  get name(): string { return this._name(); }
  set name(value: string) { this._name.set(value); }

  private readonly _disabled = signal(false);
  @Input()
  get disabled(): boolean { return this._disabled(); }
  set disabled(value: boolean) { this._disabled.set(value); }

  /** When true (default), typing filters items by `displayWith(item)`. */
  private readonly _filterable = signal(true);
  @Input()
  get filterable(): boolean { return this._filterable(); }
  set filterable(value: boolean) { this._filterable.set(value); }

  /** Extra class applied to the dropdown `<ul>` (legacy `data-dropdown-class`). */
  private readonly _dropdownClass = signal('');
  @Input()
  get dropdownClass(): string { return this._dropdownClass(); }
  set dropdownClass(value: string) { this._dropdownClass.set(value); }

  /** Function that returns the searchable string for each item. Default: `item.name`. */
  private readonly _displayWith = signal<(item: T) => string>(
    (item) => String((item as { name?: unknown } | null)?.name ?? ''),
  );
  @Input()
  get displayWith(): (item: T) => string { return this._displayWith(); }
  set displayWith(value: (item: T) => string) { this._displayWith.set(value); }

  @Output() selectionChange = new EventEmitter<T>();

  @ContentChild('itemTemplate', { static: false })
  itemTemplate?: TemplateRef<{ $implicit: T; query: string }>;

  @HostBinding('class.acme-combobox') readonly baseClass = true;

  isOpen = signal(false);
  query = signal('');

  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

  readonly filteredItems = computed(() => {
    const list = this._items() ?? [];
    if (!this._filterable() || !this.query()) {
      return list;
    }
    const q = this.query().toLowerCase();
    return list.filter((item) => this._displayWith()(item).toLowerCase().includes(q));
  });

  toggle(): void {
    if (this._disabled()) {
      return;
    }
    this.isOpen.set(!this.isOpen());
  }

  open(): void {
    if (this._disabled() || this.isOpen()) {
      return;
    }
    this.isOpen.set(true);
  }

  onQueryChange(value: string): void {
    this.query.set(value);
    if (!this.isOpen()) {
      this.isOpen.set(true);
    }
  }

  selectItem(item: T): void {
    if (this._disabled() || item == null) {
      return;
    }
    this.selectionChange.emit(item);
    this.query.set('');
    this.isOpen.set(false);
  }

  @HostListener('document:click', ['$event.target'])
  onDocumentClick(target: EventTarget | null): void {
    if (!this.isOpen()) {
      return;
    }
    if (target instanceof Node && !this.host.nativeElement.contains(target)) {
      this.isOpen.set(false);
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.isOpen()) {
      this.isOpen.set(false);
    }
  }
}
